import ButtonPrimary from '@components/common/ButtonPrimary';
import cabanaIcon from '@assets/images/cabana-icon-3.svg';
import drinkIcon from '@assets/images/drink-icon-2.svg';
import mortarIcon from '@assets/images/mortar-icon.svg';
import giftCard from '@assets/images/gift-card-holiday.svg';
import React from 'react';
import Image from 'next/legacy/image';
import RoutingPath from '@constants/routingPath';
import { useRouter } from 'next/router';
import Accordion from '@components/common/Accordion/Accordion';
import Link from 'next/link';
import { buyGiftCardClicked } from '@events/globals';
import Head from 'next/head';
import styles from './index.module.css';

const pageTitle = 'ResortPass | Gift Card - The perfect gift is a luxury daycation';

export default function GiftCardsPage() {
  const router = useRouter();

  const handleCTAClick = () => {
    // Event
    buyGiftCardClicked();
    router.push(`${RoutingPath.GIFT_CARDS}/buy`);
  };

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} key="title" />
        <meta
          name="description"
          content="Give the gift of a luxury daycation with ResortPass gift cards. Perfect for accessing top hotel amenities like pools, spas, and private beaches. Buy now and send instantly!"
        />
      </Head>
      <div className="flex flex-col">
        {/* Hero Section */}
        <div className="bg-sky-blue-gradient-3">
          <div className={styles.hero}>
            <div className={styles.heroLeftContainer}>
              <p className={styles.heroText}>The perfect gift is a luxury daycation</p>

              <div className="flex items-center justify-center mt-8 d:justify-start">
                <ButtonPrimary onClick={handleCTAClick} classes="max-w-175">
                  Gift ResortPass
                </ButtonPrimary>
              </div>
            </div>

            <div className={styles.heroImageContainer}>
              <div className={styles.heroImage}>
                <Image src={giftCard} layout="fill" objectPosition="cover" alt="Gift ResortPass" />
              </div>
            </div>
          </div>
        </div>

        {/* Perks Section */}
        <div className="flex flex-col items-center justify-center p-14 w-full">
          <p className="-tracking-0.75 font-rp-cera-medium leading-35px text-28 text-center text-rp-admin-dark d:-tracking-1 d:leading-60px d:text-40px">
            The gift of escape...without leaving town
          </p>

          <div className="flex flex-col items-baseline mt-12 space-y-14 d:flex-row d:space-y-0 d:space-x-16 d:mt-16">
            <div className="flex flex-col items-center justify-center d:max-w-290">
              <div className="h-12 relative w-12 d:h-20 d:w-20">
                <Image src={cabanaIcon} layout="fill" alt="In non mauris vel in" />
              </div>
              <p className="font-rp-cera-medium mt-6 text-2xl text-center text-rp-admin-dark -tracking-0.24 d:text-21">
                The Daycation Experience
              </p>
              <p className="font-rp-pn-regular leading-26px mt-2 text-17 text-center text-rp-admin-dark">
                Access the best luxury hotel and resort amenities including breathtaking pools,
                private beaches, and deluxe spas without leaving town. (Or take a getaway to the
                next level!)
              </p>
            </div>

            <div className="flex flex-col items-center justify-center d:max-w-290">
              <div className="h-12 relative w-12 d:h-20 d:w-20">
                <Image src={drinkIcon} layout="fill" alt="In non mauris vel in" />
              </div>
              <p className="font-rp-cera-medium mt-6 text-2xl text-center text-rp-admin-dark -tracking-0.24 d:text-21">
                The World&apos;s Best Hotels
              </p>
              <p className="font-rp-pn-regular leading-26px mt-2 text-17 text-center text-rp-admin-dark">
                Get day access to over 900 hotels and resorts across 250+ cities. Whether
                you&apos;re seeking revel or rest, you&apos;ll find the perfect match.
              </p>
            </div>

            <div className="flex flex-col items-center justify-center d:max-w-290">
              <div className="h-12 relative w-12 d:h-20 d:w-20">
                <Image src={mortarIcon} layout="fill" alt="In non mauris vel in" />
              </div>
              <p className="font-rp-cera-medium mt-6 text-2xl text-center text-rp-admin-dark -tracking-0.24 d:text-21">
                Use It Anywhere
              </p>
              <p className="font-rp-pn-regular leading-26px mt-2 text-17 text-center text-rp-admin-dark">
                Book the perfect daycation with our app instantly from anywhere. Or find us on the
                web.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-rp-primary-sky-blue flex flex-col items-center justify-center px-10 py-14 w-full d:py-24">
          <p className="font-rp-cera-medium leading-43.5px text-4xl text-center text-rp-admin-dark -tracking-0.75 d:text-40px d:-tracking-1 d:leading-50px d:max-w-400">
            Choose any amount. <p>We&apos;ll send it.</p>
          </p>

          <div className="mt-6">
            <ButtonPrimary onClick={handleCTAClick} classes="px-8">
              Buy a Gift Card
            </ButtonPrimary>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-rp-admin-dark flex flex-col items-center justify-center p-14 px-6 w-full d:flex-row d:items-baseline d:p-20 d:space-x-16">
          <p className="-tracking-0.75 font-rp-cera-medium leading-35px max-w-328 text-28 text-center text-white d:text-40px d:leading-60px d:-tracking-1 d:max-w-424 d:text-left">
            Frequently Asked Questions
          </p>

          <div className="mt-12 w-full d:max-w-735">
            <div className="flex flex-col space-y-5 text-left">
              <Accordion classes="border-b border-opacity-30 border-white pb-5">
                <Accordion.Title buttonClass="w-full">
                  <div className="-tracking-0.5 flex font-rp-cera-regular items-center justify-between text-17 text-white d:text-18">
                    <p>What is ResortPass?</p>
                  </div>
                </Accordion.Title>
                <Accordion.Content>
                  <div className="mt-3">
                    <p className="-tracking-0.5 font-rp-cera-regular opacity-70 text-base text-white">
                      ResortPass offers day access to luxury hotel amenities, empowering people to
                      experience crystalline pools, private beaches, deluxe spas, top fitness
                      facilities, and more at leading hotels in their own neighborhoods. (It&apos;s
                      also great while traveling!). Find out more about ResortPass in our{' '}
                      <Link
                        href="https://www.resortpass.com/blog"
                        className="underline"
                        target="_blank"
                      >
                        blog
                      </Link>
                    </p>
                  </div>
                </Accordion.Content>
              </Accordion>

              <Accordion classes="border-b border-opacity-30 border-white pb-5">
                <Accordion.Title buttonClass="w-full">
                  <div className="-tracking-0.5 flex font-rp-cera-regular items-center justify-between text-17 text-white d:text-18">
                    <p>Are gift cards physical or digital?</p>
                  </div>
                </Accordion.Title>

                <Accordion.Content>
                  <div className="mt-3">
                    <p className="-tracking-0.5 font-rp-cera-regular opacity-70 text-base text-white">
                      Gift cards bought on ResortPass.com are digital only. When you make a purchase
                      we&apos;ll email you a printable version of a gift card that you can email
                      forward or gift in person. Or we can email the recipient directly if you
                      provide their name and email at the time of purchase.
                    </p>
                  </div>
                </Accordion.Content>
              </Accordion>

              <Accordion classes="border-b border-opacity-30 border-white pb-5">
                <Accordion.Title buttonClass="w-full">
                  <div className="-tracking-0.5 flex font-rp-cera-regular items-center justify-between text-17 text-white d:text-18">
                    <p>What can gift cards be used for?</p>
                  </div>
                </Accordion.Title>

                <Accordion.Content>
                  <div className="mt-3">
                    <p className="-tracking-0.5 font-rp-cera-regular opacity-70 text-base text-white">
                      Gift cards can be used to book any offering on ResortPass.
                    </p>
                  </div>
                </Accordion.Content>
              </Accordion>

              <Accordion classes="border-b border-opacity-30 border-white pb-5">
                <Accordion.Title buttonClass="w-full">
                  <div className="-tracking-0.5 flex font-rp-cera-regular items-center justify-between text-17 text-white d:text-18">
                    <p>How do you redeem a gift?</p>
                  </div>
                </Accordion.Title>

                <Accordion.Content>
                  <div className="mt-3">
                    <p className="-tracking-0.5 font-rp-cera-regular opacity-70 text-base text-white">
                      When you receive a ResortPass gift, you&apos;ll get an email with instructions
                      on how to redeem it. You can visit also{' '}
                      <Link href={`${RoutingPath.GIFT_CARDS}/redeem`} className="underline">
                        https://www.resortpass.com/giftcards/redeem
                      </Link>{' '}
                      and enter the claim code for your ResortPass Gift Card, and credit will be
                      available for your next reservation.
                    </p>
                  </div>
                </Accordion.Content>
              </Accordion>

              <Accordion classes="border-b border-opacity-30 border-white pb-5">
                <Accordion.Title buttonClass="w-full">
                  <div className="-tracking-0.5 flex font-rp-cera-regular items-center justify-between text-17 text-white d:text-18">
                    <p>Do gift cards expire?</p>
                  </div>
                </Accordion.Title>

                <Accordion.Content>
                  <div className="mt-3">
                    <p className="-tracking-0.5 font-rp-cera-regular opacity-70 text-base text-white">
                      They don&apos;t! Redeem and use ResortPass gift cards at any time.
                    </p>
                  </div>
                </Accordion.Content>
              </Accordion>

              <Accordion classes="border-b border-opacity-30 border-white pb-5">
                <Accordion.Title buttonClass="w-full">
                  <div className="-tracking-0.5 flex font-rp-cera-regular items-center justify-between text-17 text-white d:text-18">
                    <p>Can I return a gift?</p>
                  </div>
                </Accordion.Title>

                <Accordion.Content>
                  <div className="mt-3">
                    <p className="-tracking-0.5 font-rp-cera-regular opacity-70 text-base text-white">
                      Sorry, we don&apos;t offer returns on gift cards.
                    </p>
                  </div>
                </Accordion.Content>
              </Accordion>

              <Accordion classes="border-b border-opacity-30 border-white pb-5">
                <Accordion.Title buttonClass="w-full">
                  <div className="-tracking-0.5 flex font-rp-cera-regular items-center justify-between text-17 text-white d:text-18">
                    <p>What are the terms and conditions?</p>
                  </div>
                </Accordion.Title>

                <Accordion.Content>
                  <div className="mt-3">
                    <p className="-tracking-0.5 font-rp-cera-regular opacity-70 text-base text-white">
                      ResortPass gift card funds do not expire and can only be redeemed on{' '}
                      <Link href="/" className="underline">
                        ResortPass.com
                      </Link>{' '}
                      or using the ResortPass mobile apps. No fees for purchase or activation of the
                      Card. ResortPass Gift Cards cannot be redeemed at the hotel. The Card is
                      non-reloadable and, except where required by law, cannot be redeemed for cash,
                      refunded, or returned. Treat this Card as cash. ResortPass is not responsible
                      for lost, damaged or stolen cards, or for unauthorized use. For Card and
                      balance info, call (510) 681-2548 or visit{' '}
                      <Link href="/" className="underline">
                        ResortPass.com
                      </Link>
                      . Terms apply and are subject to change without notice.
                    </p>
                  </div>
                </Accordion.Content>
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
